import { useOutletContext, useParams, Link } from 'react-router-dom';
import type { RackCatalogue } from '../lib/types';
import { money, weight } from '../lib/format';
import { ProductThumb } from './ProductThumb';
import { EmptyState } from '../components/ui';
import { useCart } from '../store/cart';
import { useToast } from '../components/Toast';

export function RackPage() {
  const { qrToken } = useParams();
  const { catalogue } = useOutletContext<{ catalogue: RackCatalogue }>();
  const add = useCart((s) => s.add);
  const toast = useToast();

  if (!catalogue) return null;
  const { products, rack } = catalogue;

  return (
    <div className="px-4 pb-24 pt-3">
      {rack.description && (
        <p className="mb-3 rounded-lg bg-gold-50 px-3 py-2 text-sm text-gold-700">{rack.description}</p>
      )}
      <div className="mb-3 flex items-baseline justify-between">
        <h1 className="text-lg font-semibold text-ink-900">The Collection</h1>
        <span className="text-xs text-ink-400">{products.length} pieces</span>
      </div>

      {products.length === 0 ? (
        <EmptyState title="No pieces on this rack yet" hint="Please check with our staff at the stall." />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {products.map((p) => (
            <div key={p.assignmentId} className="card overflow-hidden">
              <Link to={`/r/${qrToken}/p/${p.assignmentId}`} className="block">
                <div className="aspect-square bg-ink-50">
                  <ProductThumb src={p.image?.thumbnailUrl} alt={p.name} label={p.photoId} />
                </div>
              </Link>
              <div className="space-y-1 p-2.5">
                <Link to={`/r/${qrToken}/p/${p.assignmentId}`}>
                  <p className="line-clamp-1 text-sm font-medium text-ink-900">{p.name}</p>
                </Link>
                <p className="text-[11px] text-ink-400">
                  #{p.barcodeNo}
                  {p.size ? ` · Size ${p.size}` : ''}
                </p>
                <p className="text-[11px] text-ink-400">
                  {weight(p.grossWeight)} gross
                  {p.stoneWeight ? ` · ${weight(p.stoneWeight)} stone` : ''}
                </p>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-semibold text-gold-600">
                    {p.price ? money(p.price) : 'Enquire'}
                  </span>
                  {p.inStock ? (
                    <button
                      className="rounded-md bg-ink-900 px-2 py-1 text-[11px] font-medium text-white active:bg-ink-700"
                      onClick={() => {
                        add(p, rack.number);
                        toast('Added to cart', 'success');
                      }}
                    >
                      Add
                    </button>
                  ) : (
                    <span className="text-[11px] font-medium text-red-500">Sold out</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
